// src/pages/ServicesPage.jsx
import React, { useRef, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  useTheme,
  Link,
  Avatar,
  IconButton,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiHome,
  FiTrendingUp,
  FiMonitor,
  FiSettings,
  FiZap,
  FiShield,
  FiArrowRightCircle,
  FiChevronLeft,
  FiChevronRight,
  FiBriefcase,
  FiShoppingBag,
  FiTool,
} from "react-icons/fi";
import ScrollFade from "../components/ScrollFade";

/* ---------- Assets ---------- */
import heroImg from "../assets/paneles-campo.jpg";
import logoFin from "../assets/logo-renovado.png";

/* ---------- Data ---------- */
const CORE_SERVICES = [
  {
    icon: FiHome,
    title: "Diseño solar personalizado",
    desc: "Ingeniería 3D y simulación de ahorro adaptada a tu techo y consumo.",
  },
  {
    icon: FiTrendingUp,
    title: "Financiamiento flexible",
    desc: "Planes 12-60 meses; tu ahorro cubre la mensualidad desde el día 1.",
  },
  {
    icon: FiMonitor,
    title: "Monitoreo & mantenimiento 24/7",
    desc: "App IoT, alertas instantáneas y servicio técnico en ≤ 2 h.",
  },
  {
    icon: FiSettings,
    title: "Instalación profesional",
    desc: "Técnicos certificados y puesta en marcha sin interrupciones.",
  },
  {
    icon: FiZap,
    title: "Ahorro mínimo garantizado",
    desc: "Contrato SLA con metas de producción o penalización.",
  },
  {
    icon: FiShield,
    title: "Garantía de 25 años",
    desc: "Paneles Tier-1 y respaldo escrito de generación.",
  },
];

const MODELOS = [
  {
    logo: logoFin,
    title: "PPA / Energía como servicio",
    desc: "Sin inversión inicial; paga solo los kWh que consumes y ahorra desde el primer mes.",
  },
  {
    logo: logoFin,
    title: "Propiedad financiada",
    desc: "Financia tu planta y capitaliza el 100 % del ahorro a largo plazo.",
  },
];

const INDUSTRIAS = [
  { icon: FiShoppingBag, label: "Comercio minorista" },
  { icon: FiTool, label: "Industria ligera" },
  { icon: FiBriefcase, label: "Oficinas corporativas" },
  { icon: FiHome, label: "Residencial" },
];

/* ---------- Animations ---------- */
const itemUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: d, duration: 0.5 },
  }),
};

/* ---------- Accent palette ---------- */
const ACCENTS = [
  "#FF8A00",
  "#FFBC5E",
  "#3B82F6",
  "#41B883",
  "#EF4444",
  "#7C3AED",
];

/* ===============================================================
   SnapCarousel – “Ponemos el sol a tu servicio”
   ============================================================== */
function SnapCarousel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const trackRef = useRef(null);
  const [edge, setEdge] = useState({ left: true, right: false });

  const scanEdges = (el) =>
    setEdge({
      left: el.scrollLeft === 0,
      right: Math.ceil(el.scrollLeft + el.offsetWidth) >= el.scrollWidth,
    });

  const scrollBy = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const gap = parseInt(getComputedStyle(track).columnGap || 0, 10);
    const cardW = track.firstChild.offsetWidth + gap;
    track.scrollBy({ left: dir * cardW, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        position: "relative",
        py: { xs: 10, md: 14 },
        background:
          "linear-gradient(135deg,#ffffff 0%,#fff7ec 45%,#fff4e7 100%)",
        overflow: "hidden",
      }}
    >
      {/* halos */}
      <Box
        sx={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          filter: "blur(140px)",
          background: "rgba(255,173,51,.45)",
          top: -120,
          left: -120,
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 360,
          height: 360,
          borderRadius: "50%",
          filter: "blur(120px)",
          background: "rgba(255,106,0,.35)",
          bottom: -100,
          right: -100,
          zIndex: 0,
        }}
      />

      <Typography
        variant="h4"
        fontWeight={900}
        align="center"
        gutterBottom
        sx={{ position: "relative", zIndex: 1 }}
      >
        Ponemos el sol a tu servicio
      </Typography>
      <Typography
        align="center"
        sx={{
          maxWidth: 720,
          mx: "auto",
          mb: 7,
          color: "text.secondary",
          position: "relative",
          zIndex: 1,
        }}
      >
        Desliza o usa las flechas para conocer cada etapa.
      </Typography>

      {!isMobile && (
        <>
          <IconButton
            disabled={edge.left}
            onClick={() => scrollBy(-1)}
            sx={{
              position: "absolute",
              left: 40,
              top: "50%",
              transform: "translateY(-50%)",
              backdropFilter: "blur(8px)",
              bgcolor: "rgba(255,255,255,.6)",
              zIndex: 3,
              "&:hover": { bgcolor: "rgba(255,255,255,.8)" },
              boxShadow: "0 4px 14px rgba(0,0,0,.1)",
              opacity: edge.left ? 0.4 : 1,
            }}
          >
            <FiChevronLeft size={24} />
          </IconButton>
          <IconButton
            disabled={edge.right}
            onClick={() => scrollBy(1)}
            sx={{
              position: "absolute",
              right: 40,
              top: "50%",
              transform: "translateY(-50%)",
              backdropFilter: "blur(8px)",
              bgcolor: "rgba(255,255,255,.6)",
              zIndex: 3,
              "&:hover": { bgcolor: "rgba(255,255,255,.8)" },
              boxShadow: "0 4px 14px rgba(0,0,0,.1)",
              opacity: edge.right ? 0.4 : 1,
            }}
          >
            <FiChevronRight size={24} />
          </IconButton>
        </>
      )}

      <Box
        ref={trackRef}
        onScroll={(e) => scanEdges(e.target)}
        sx={{
          maxWidth: { xs: "100%", md: 900 },
          mx: "auto",
          px: { xs: 2, md: 0 },
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          columnGap: { xs: 3, md: 4 },
          pb: 1,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {CORE_SERVICES.map((svc, idx) => (
          <motion.div
            key={svc.title}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{
              opacity: 1,
              scale: 1,
              transition: { duration: 0.5 },
            }}
            viewport={{ once: true, amount: 0.5 }}
            style={{ flex: "0 0 80%", scrollSnapAlign: "center" }}
          >
            <Box
              sx={{
                position: "relative",
                p: { xs: 4, md: 5 },
                bgcolor: "#fff",
                borderRadius: 5,
                boxShadow: "0 14px 40px rgba(0,0,0,.06)",
                textAlign: "center",
                transition: "transform .35s",
                "&:hover": { transform: "translateY(-8px)" },
                overflow: "hidden",
                "::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: 5,
                  background: `linear-gradient(145deg,${ACCENTS[idx]}22 0%,transparent 70%)`,
                  zIndex: 0,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 84,
                  height: 84,
                  mx: "auto",
                  mb: 2.5,
                  fontSize: 40,
                  bgcolor: `${ACCENTS[idx]}22`,
                  color: ACCENTS[idx],
                  zIndex: 1,
                }}
              >
                <svc.icon />
              </Avatar>
              <Typography
                variant="h6"
                fontWeight={800}
                gutterBottom
                sx={{ position: "relative", zIndex: 1 }}
              >
                {svc.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ position: "relative", zIndex: 1 }}
              >
                {svc.desc}
              </Typography>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
}

/* ===============================================================
   IndustriesSection – hexágonos
   ============================================================== */
function IndustriesSection() {
  const theme = useTheme();
  return (
    <Box sx={{ py: { xs: 9, md: 14 }, position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          width: 320,
          height: 320,
          top: -80,
          left: -80,
          transform: "rotate(20deg)",
          backgroundImage:
            "radial-gradient(rgba(0,0,0,.05) 1px, transparent 1px)",
          backgroundSize: "12px 12px",
        }}
      />
      <Container maxWidth="md">
        <Typography variant="h4" fontWeight={800} align="center" gutterBottom>
          Industrias que confían en nosotros
        </Typography>
        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          justifyContent="center"
          sx={{ mt: 4 }}
        >
          {INDUSTRIAS.map((ind, idx) => (
            <Grid key={ind.label} item xs={6} sm={3}>
              <motion.div
                variants={itemUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  component={motion.div}
                  whileHover={{ rotateY: 12, rotateX: -6, scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 140, damping: 12 }}
                  sx={{
                    width: 110,
                    height: 110,
                    clipPath:
                      "polygon(25% 4%,75% 4%,100% 50%,75% 96%,25% 96%,0% 50%)",
                    background: `${ACCENTS[idx]}22`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    mb: 2,
                    boxShadow: "0 10px 25px rgba(0,0,0,.12)",
                    "@media (max-width:370px)": { clipPath: "circle(45%)" },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      borderRadius: "inherit",
                      clipPath: "inherit",
                      background: `radial-gradient(circle at 50% 30%,${ACCENTS[idx]}33 0%,transparent 70%)`,
                      zIndex: -1,
                    },
                  }}
                >
                  <ind.icon size={30} color={ACCENTS[idx]} />
                </Box>
                <Typography fontWeight={600} align="center">
                  {ind.label}
                </Typography>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

/* ===============================================================
   Página principal
   ============================================================== */
export default function ServicesPage() {
  const theme = useTheme();
  return (
    <Box component="main" sx={{ overflowX: "hidden" }}>
      {/* HERO */}
      <Box
        sx={{
          height: "100dvh",
          display: "flex",
          alignItems: "center",
          color: "#fff",
          background: `linear-gradient(rgba(0,0,0,.55),rgba(0,0,0,.55)),url(${heroImg}) center/cover`,
        }}
      >
        <Container sx={{ position: "relative", zIndex: 2 }}>
          <ScrollFade variants={itemUp} initial="hidden" animate="visible">
            <Typography
              variant="h2"
              fontWeight={900}
              gutterBottom
              sx={{
                textShadow: "0 4px 14px rgba(0,0,0,.6)",
                fontSize: { xs: 36, md: 60 },
              }}
            >
              Ahorra hasta un&nbsp;95&nbsp;% en tu recibo de luz
            </Typography>
            <Typography sx={{ maxWidth: 520, mb: 4, opacity: 0.9 }}>
              Soluciones solares llave en mano para hogares, negocios e
              industria en Sonora.
            </Typography>
            <Button
              component={NavLink}
              to="/cotizar"
              size="large"
              variant="contained"
              sx={{
                py: 1.8,
                px: 5,
                fontWeight: 700,
                borderRadius: 3,
                backgroundImage: `linear-gradient(135deg,${theme.palette.primary.main} 0%,${theme.palette.primary.dark} 100%)`,
              }}
            >
              Solicitar propuesta
            </Button>
            <Box mt={3}>
              <Link
                component={NavLink}
                to="/"
                color="white"
                underline="hover"
                sx={{ mr: 1, fontWeight: 500 }}
              >
                Inicio
              </Link>
              <Typography component="span" color="white" sx={{ opacity: 0.8 }}>
                / Servicios
              </Typography>
            </Box>
          </ScrollFade>
        </Container>
      </Box>

      {/* Snap-carousel */}
      <SnapCarousel />

      {/* MODELOS DE NEGOCIO (glass-cards legibles) */}
      <Box
        sx={{
          py: { xs: 9, md: 14 },
          position: "relative",
          bgcolor: theme.palette.grey[900],
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-20%",
            left: "50%",
            width: 900,
            height: 900,
            transform: "translateX(-50%)",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,.06) 0%, transparent 70%)",
            filter: "blur(120px)",
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h4"
            fontWeight={800}
            align="center"
            color="#fff"
            gutterBottom
          >
            Nuestros modelos de negocio
          </Typography>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 4, md: 6 },
              justifyContent: "center",
              alignItems: "stretch",
            }}
          >
            {MODELOS.map((m) => (
              <motion.div
                key={m.title}
                variants={itemUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                style={{ flex: 1, maxWidth: 480 }}
              >
                <Box
                  sx={{
                    p: { xs: 4, md: 5 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    height: "100%",
                    bgcolor: alpha("#ffffff", 0.12),
                    border: "1px solid rgba(255,255,255,0.15)",
                    color: "#fff",
                    backdropFilter: "blur(6px)",
                    borderRadius: 4,
                    boxShadow: "0 15px 40px rgba(0,0,0,.4)",
                    transition:
                      "transform .25s, box-shadow .25s, background-color .25s",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 48px rgba(0,0,0,.55)",
                      bgcolor: alpha("#ffffff", 0.18),
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={m.logo}
                    alt={m.title}
                    sx={{ width: 76, height: "auto", mb: 2 }}
                  />
                  <Typography variant="h5" fontWeight={800}>
                    {m.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flexGrow: 1, opacity: 0.9 }}
                  >
                    {m.desc}
                  </Typography>
                  <Button
                    endIcon={<FiArrowRightCircle />}
                    sx={{
                      alignSelf: "flex-start",
                      py: 1,
                      px: 3,
                      fontWeight: 700,
                      bgcolor: "#fff",
                      color: theme.palette.grey[900],
                      "&:hover": { bgcolor: "#f6f6f6" },
                    }}
                  >
                    Conocer más
                  </Button>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Industrias */}
      <IndustriesSection />

      {/* CTA FINAL */}
      <Box
        sx={{
          py: { xs: 9, md: 14 },
          textAlign: "center",
          bgcolor: theme.palette.primary.main,
          color: "#fff",
        }}
      >
        <Container maxWidth="sm">
          <motion.div
            variants={itemUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <Typography variant="h4" fontWeight={900} gutterBottom>
              ¿Listo para tu proyecto solar?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>
              Solicita tu cotización sin compromiso y descubre tu ahorro real.
            </Typography>
            <Button
              component={NavLink}
              to="/cotizar"
              size="large"
              variant="contained"
              sx={{
                bgcolor: "#fff",
                color: theme.palette.primary.main,
                fontWeight: 700,
                px: 5,
                py: 1.6,
                borderRadius: 3,
                "&:hover": { bgcolor: "#f6f6f6" },
              }}
            >
              Cotizar ahora
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}
